/**
 * LA PAUSA CONCORDATA del backgammon (ADR-0176) — la logica pura della schermata.
 *
 * Luca, testuale (terminale di flotta, 2026-09-28):
 * · I-248 15:52:24Z — «Nel backgammon voglio la possibilità di proporre pausa ad avversario durante
 *   game per una determinata durata - i giocatori possono riprendere dalla pausa prima se entrambi
 *   d'accordo, oppure il gioco riprende automaticamente quando la pausa scade.»
 * · I-249 15:59:34Z — durate «2 / 5 / 10 e un dato input da parte giocatore per proporre altra durata
 *   espressa in numero di ore»; quante «Illimitate»; dove «Tutte tranne tornei».
 * · L-2409 «(a) solo chi ha il turno, prima di tirare; senza risposta in 30 s vale come rifiutata»
 * · L-2410 «(a) «In pausa fino alle HH:MM» con gli orologi fermi»
 * · L-2417 «(c) subito tutto, ore comprese, con l'avviso a schermo che un aggiornamento del server
 *   annulla la pausa»
 *
 * Lo STATO viene dal server, solo nello snapshot (`match:state.pause`, QGammon #97): non esiste un
 * evento di pausa. Qui si legge e si decide cosa dire; il componente e' `PausaConcordata.tsx`.
 *
 * ⚠️ `state` e' `unknown`, per la stessa ragione di `orologio.ts`: board-ui non fa il parse dello
 * snapshot (il repo teneva `@qlashgeminiz/qgammon-types` 0.2.0 al livello alto, senza il campo; dal
 * 29/09 e' 0.16.0, ma il controllo a runtime resta l'unico che esiste, e sta qui).
 */

export type Lato = 'W' | 'B';

/** La pausa come la usa la schermata: gli istanti gia' in epoch ms. */
export interface Pausa {
  readonly id: string;
  readonly stato: 'proposta' | 'in_pausa';
  /** Chi l'ha proposta: c'e' SOLO per i giocatori (lo spettatore non la riceve). */
  readonly by: Lato | null;
  readonly durataMs: number;
  readonly scadenzaProposta: number | null;
  readonly fino: number | null;
  readonly ripresaChiestaDa: Lato | null;
}

const MINUTO = 60_000;
/** I 30 s di una proposta (L-2409), gli stessi di `PAUSE_PROPOSAL_TTL_MS` di types 0.15.0. */
export const TTL_PROPOSTA_MS = 30_000;
const ORA = 3_600_000;

/** Le tre durate fisse, in minuti (I-249). Le stesse di `PAUSE_PRESET_MINUTES` di types 0.15.0. */
export const DURATE_MINUTI = [2, 5, 10] as const;
/**
 * Il tetto delle ore (L-2407 (c)). E' il DEFAULT del server (`PAUSE_HOURS_MAX`, che puo' solo
 * abbassarlo): se il server ne ammette meno, risponde `PAUSE_NOT_ALLOWED` e la schermata lo dice.
 */
export const ORE_MAX = 24;

function lato(v: unknown): Lato | null {
  return v === 'W' || v === 'B' ? v : null;
}

function istante(v: unknown): number | null {
  if (typeof v !== 'string') return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

/**
 * La pausa dello snapshot, o `null` — anche per una forma che non riconosco: meglio non dire
 * «in pausa» che dirlo su un dato che non so leggere.
 */
export function leggiPausa(state: unknown): Pausa | null {
  if (!state || typeof state !== 'object') return null;
  const p = (state as { pause?: unknown }).pause;
  if (!p || typeof p !== 'object') return null;
  const o = p as Record<string, unknown>;
  if (typeof o.id !== 'string' || o.id.length === 0) return null;
  if (o.stato !== 'proposta' && o.stato !== 'in_pausa') return null;
  if (typeof o.durataMs !== 'number' || !Number.isFinite(o.durataMs) || o.durataMs <= 0) return null;
  const fino = istante(o.fino);
  const scadenzaProposta = istante(o.scadenzaProposta);
  // Ogni stato ha il SUO istante: senza, non c'e' niente di vero da mostrare.
  if (o.stato === 'in_pausa' && fino === null) return null;
  if (o.stato === 'proposta' && scadenzaProposta === null) return null;
  return {
    id: o.id,
    stato: o.stato,
    by: lato(o.by),
    durataMs: o.durataMs,
    scadenzaProposta,
    fino,
    ripresaChiestaDa: lato(o.ripresaChiestaDa),
  };
}

/** `true` se la partita e' in pausa concordata (accettata). */
export function inPausa(state: unknown): boolean {
  return leggiPausa(state)?.stato === 'in_pausa';
}

/** Una pausa di ORE intere: quella che un aggiornamento del server annulla (L-2417). */
export function pausaInOre(durataMs: number): boolean {
  return durataMs >= ORA && durataMs % ORA === 0;
}

/** Durata di una scelta del foglio, in ms: minuti fissi o ore intere 1..ORE_MAX. `null` se non valida. */
export function durataDa(scelta: { minuti: number } | { ore: number }): number | null {
  if ('minuti' in scelta) {
    return (DURATE_MINUTI as readonly number[]).includes(scelta.minuti) ? scelta.minuti * MINUTO : null;
  }
  const n = scelta.ore;
  return Number.isInteger(n) && n >= 1 && n <= ORE_MAX ? n * ORA : null;
}

/** «HH:MM» dell'istante, nell'ora LOCALE del dispositivo (L-2410). */
export function oraLocale(istanteMs: number, lingua: string): string {
  // `hourCycle: 'h23'` (revisione di #497): su motori vecchi `en` + `hour12: false` dava «24:00».
  return new Intl.DateTimeFormat(lingua, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    new Date(istanteMs),
  );
}

/**
 * Di QUALE giorno e' «fino alle HH:MM» (🟡 (4) del collaudo di #493): L-2410 dice «l'ora locale», e
 * presa alla lettera una pausa di 3 ore alle 23:00 diceva «fino alle 02:00» — cioe' fra 21 ore, a
 * leggerla. Si confrontano i giorni di CALENDARIO locali (non le 24 ore): `oggi`, `domani`, oppure
 * `altro` (oltre domani), che porta anche la data corta. `altro` e' un caso VERO, non solo di orologi
 * strani: 24 ore dalle 23:30 del 28/03 finiscono il 30/03 (revisione di #497, Y7).
 */
export function giornoDellaFine(
  istanteMs: number,
  adessoMs: number,
  lingua: string,
): { giorno: 'oggi' | 'domani' | 'altro'; data: string } {
  const inizioGiorno = (ms: number) => {
    const d = new Date(ms);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  };
  // Math.round, non ceil ne' floor: il giorno del cambio d'ora dura 25 ore (fine ottobre: 25 h ⇒ 1,04)
  // o 23 (fine marzo: 23 h ⇒ 0,96), e tutti e due sono UN giorno di calendario.
  const giorni = Math.round((inizioGiorno(istanteMs) - inizioGiorno(adessoMs)) / 86_400_000);
  const data = new Intl.DateTimeFormat(lingua, { day: '2-digit', month: '2-digit' }).format(new Date(istanteMs));
  return { giorno: giorni === 0 ? 'oggi' : giorni === 1 ? 'domani' : 'altro', data };
}

/**
 * I dadi sul tavolo, in pausa concordata (🟡 (3) del collaudo di #493): restavano quelli del turno
 * PRIMA, e a pausa accettata sembravano un tiro da giocare. La pausa si propone prima di tirare, quindi
 * i dadi a schermo non sono mai del turno in corso: in pausa non si VEDONO.
 * ⚠️ Non si SMONTANO (revisione di #497, Y2): `<Dice>` rinasce con `settledKey` a 0 e alla ripresa
 * rifarebbe il ruzzolone del tiro vecchio. Chi chiama tiene montato e nasconde.
 */
export function dadiInVista(base: boolean, state: unknown): boolean {
  return base && !inPausa(state);
}

/** Secondi interi rimasti fino a `istanteMs` (arrotondati in su), mai sotto zero. */
export function secondiA(istanteMs: number, adesso: number): number {
  return Math.max(0, Math.ceil((istanteMs - adesso) / 1000));
}

/**
 * Cosa mostra la schermata a CHI GIOCA, dal suo lato. `io` null = non so ancora chi sono (prima
 * dell'ack del colore): nessuna azione, solo lo stato.
 */
export type Vista =
  | { tipo: 'nessuna' }
  | { tipo: 'propostaMia'; scadenza: number }
  | { tipo: 'propostaSua'; id: string; scadenza: number; durataMs: number }
  | { tipo: 'inPausa'; id: string; fino: number; durataMs: number; ripresa: 'nessuno' | 'io' | 'avversario' };

export function vistaPausa(p: Pausa | null, io: Lato | null): Vista {
  if (!p) return { tipo: 'nessuna' };
  if (p.stato === 'in_pausa') {
    const ripresa =
      p.ripresaChiestaDa === null || io === null ? 'nessuno' : p.ripresaChiestaDa === io ? 'io' : 'avversario';
    return { tipo: 'inPausa', id: p.id, fino: p.fino as number, durataMs: p.durataMs, ripresa };
  }
  // Una proposta si attribuisce solo sapendo chi sono E chi l'ha fatta: altrimenti non la offro in
  // risposta (rispondere a una mia la rifiuterebbe il server) e non dico «in attesa».
  if (io === null || p.by === null) return { tipo: 'nessuna' };
  return p.by === io
    ? { tipo: 'propostaMia', scadenza: p.scadenzaProposta as number }
    : { tipo: 'propostaSua', id: p.id, scadenza: p.scadenzaProposta as number, durataMs: p.durataMs };
}

/**
 * Com'e' finita la MIA proposta, quando sparisce (L-2409: senza risposta vale come rifiutata, ma a
 * chi la fa i due casi si dicono diversi). Il server manda solo `pause: null` in entrambi i casi:
 * la differenza la fa l'ora — prima di `scadenzaProposta` qualcuno ha risposto «no», dopo e' scaduta.
 *
 * `null` (niente da dire) quando: non era una mia proposta; e' diventata una pausa (accettata);
 * oppure la posizione e' cambiata (`stessaPosizione` false) — l'ho annullata io tirando o
 * raddoppiando, e non e' un «rifiuto» dell'avversario.
 * ⚠️ L'ora e' quella del dispositivo contro un istante del server: uno scarto d'orologio sposta il
 * confine. Per questo si confronta con un margine, e nel dubbio vince «scaduta».
 */
export function esitoDellaMiaProposta(
  prima: Pausa | null,
  dopo: Pausa | null,
  ctx: {
    io: Lato | null;
    adesso: number;
    stessaPosizione: boolean;
    /**
     * La scadenza sull'orologio del DISPOSITIVO: l'istante in cui ho visto la proposta + 30 s
     * (revisione di #493). Se c'e', vince su `scadenzaProposta`, che e' un istante del SERVER: un
     * telefono 5 s indietro trasformerebbe uno scadere vero in «l'avversario ha rifiutato».
     */
    scadenzaLocale?: number | null;
  },
): 'rifiutata' | 'scaduta' | null {
  if (!prima || prima.stato !== 'proposta' || dopo !== null) return null;
  if (ctx.io === null || prima.by !== ctx.io) return null;
  if (!ctx.stessaPosizione) return null;
  const MARGINE_MS = 1_500;
  const scadenza = ctx.scadenzaLocale ?? prima.scadenzaProposta;
  return scadenza !== null && ctx.adesso < scadenza - MARGINE_MS ? 'rifiutata' : 'scaduta';
}

/**
 * La chiave i18n (namespace `backgammon`) della frase per un rifiuto di un comando della pausa.
 * Ogni codice ha la sua frase: mai `message` nudo, mai JSON (dispatch B3). Un codice che non
 * conosco (un server piu' nuovo) ha una frase generica, non il codice.
 * 🟡 (5) del collaudo di #493: anche i codici del TRASPORTO hanno la loro frase, e non la generica
 * «altro» (QGammon `handlers.ts`: `INTERNAL` anche da un payload che non passa lo schema, `VALIDATION`
 * da chi non e' parte della partita). ⚠️ `THROTTLED` in un ack non arriva: il server scarta il
 * pacchetto PRIMA dell'handler (`handlers.ts:397`) e board-ui risolve `NO_ANSWER` dopo 10 s. La sua
 * riga qui e' per un server che un giorno lo mettesse nell'ack.
 */
export function chiaveErrorePausa(code: string): string {
  switch (code) {
    case 'MATCH_PAUSED':
    case 'PAUSE_NOT_ALLOWED':
    case 'WRONG_PHASE':
    case 'NOT_YOUR_TURN':
    case 'NOT_CONNECTED':
    case 'NO_ANSWER':
    case 'BAD_ANSWER':
    case 'VALIDATION':
    case 'THROTTLED':
    case 'INTERNAL':
      return `match.pausa.errore.${code}`;
    default:
      return 'match.pausa.errore.altro';
  }
}

/**
 * La partita l'ha ordinata un SERVIZIO (torneo o sfida via qTournaments)? Il server rifiuta la pausa su una
 * partita cosi' (`PAUSE_NOT_ALLOWED`, ADR-0176): con `true` il bottone non si disegna.
 *
 * Il campo (`match:state.serviceOrdered`, `@qlashgeminiz/qgammon-types` 0.16.0, `board-ui` 0.11.0 lo espone
 * come `=== true`) e' un SUGGERIMENTO per la UI, e ha tre stati: `true` (ordinata da un servizio),
 * `false` (non lo e') e ASSENTE (un server piu' vecchio, o prima del primo snapshot). ⚠️ L'assente NON e'
 * `true` e nemmeno «amichevole»: vuol dire «non so», e col «non so» il bottone resta (il rifiuto del
 * server, gia' tradotto, e' la regola). Per questo `=== true` e non una verita': `1`, `'true'` o un
 * oggetto non sono un si' di nessuno, e un valore che non e' un booleano non nasconde niente.
 */
export const servizioHaOrdinato = (serviceOrdered: unknown): boolean => serviceOrdered === true;

/** I codici del trasporto (QGammon `ErrorPayloadSchema`): non dicono QUALE comando li ha causati. */
const CODICI_DEL_TRASPORTO: readonly string[] = ['VALIDATION', 'THROTTLED', 'INTERNAL'];

/**
 * Cosa fa la riga d'errore della schermata (`errorLine`) con un evento `error` che riguarda la
 * pausa. Il server manda il rifiuto DUE volte: nell'ack del comando (che `PausaConcordata` dice con
 * la sua frase) e nell'evento `error` condiviso (per i client senza ack). Senza questa funzione la
 * seconda copia comparirebbe sotto il tabellone col `message` inglese del server, nudo.
 *
 * · `MATCH_PAUSED` — un'azione di gioco rifiutata in pausa (un gesto partito nella corsa col «si'»
 *   dell'avversario): nessun comando della pausa l'ha mandata, quindi la frase la dice la riga.
 * · un rifiuto di un comando della pausa — `PAUSE_NOT_ALLOWED`, oppure `WRONG_PHASE`/`NOT_YOUR_TURN`
 *   col messaggio di un handler della pausa — la riga TACE: l'ha gia' detto il componente.
 *   ⚠️ Per questi due codici generici il riconoscimento e' sul MESSAGGIO del server, perche' il
 *   codice da solo non dice da quale handler viene. I messaggi degli handler della pausa, tutti con
 *   la parola «pause» [M QGammon `99c9e21`, `game/match.ts`]: «Only the player on roll can propose a
 *   pause», «A pause can be proposed only before rolling», «One pause proposal per turn», «No such
 *   pause proposal», «Only the opponent can answer a pause proposal», «No such pause». Se un giorno
 *   cambiano, la riga torna a mostrare il messaggio: il verso innocuo (si dice troppo, non si nasconde).
 * · tutto il resto: `null`, la riga fa come prima.
 */
export function erroreDellaPausa(
  error: { code: string; message: string } | null,
): { tipo: 'frase'; chiave: string } | { tipo: 'taci' } | null {
  if (!error) return null;
  if (error.code === 'MATCH_PAUSED') return { tipo: 'frase', chiave: chiaveErrorePausa('MATCH_PAUSED') };
  if (error.code === 'PAUSE_NOT_ALLOWED') return { tipo: 'taci' };
  if ((error.code === 'WRONG_PHASE' || error.code === 'NOT_YOUR_TURN') && /\bpause\b/i.test(error.message)) {
    return { tipo: 'taci' };
  }
  // 🟡 (5): i codici del TRASPORTO non si mostrano mai grezzi. Dalla riga non si sa quale comando li ha
  // causati (il `message` di `THROTTLED` e di un `INTERNAL` da schema non nomina la pausa), quindi
  // l'unico modo di non far uscire grezzo quello di una pausa e' non farne uscire grezzo nessuno: una
  // frase che non nomina la pausa, per qualunque comando. ⚠️ Per un comando della pausa parlano DUE
  // voci — l'ack nel componente e questa riga — e va bene cosi' (revisione di #497, Y4): nessun codice
  // del trasporto del server nomina la pausa, e un ramo che tacesse su «pause» nasconderebbe un giorno
  // l'errore di una mossa.
  if (CODICI_DEL_TRASPORTO.includes(error.code)) return { tipo: 'frase', chiave: `match.erroreServer.${error.code}` };
  return null;
}
